// Calculation settings schema
// Document-level configuration for the calculation engine

import { z } from 'zod';
import { ValueWithUnitSchema } from '../base/common';

/**
 * Calculation Settings Schema
 * Document-level configuration parameters required by the calculation engine.
 *
 * Source: C++ calc_settings_serializer.h (BOOST_CLASS_VERSION 8 in v21.0)
 * Field order matches C++ serialization order for documentation purposes.
 */
export const CalculationSettingsSchema = z.object({
  // --- Always present (version 0) ---
  calc_method: z.enum(['darcy', 'hazen']).optional(),
  tolerance: z.number().optional(),
  max_iterations: z.number().int().positive(),
  linear_switch_point: z.number(),
  laminar_cutoff_Reynolds: z.number(),
  force_laminar: z.boolean().optional(),

  // --- version >= 1 ---
  /** Allowable deviation for balancing, in percent */
  allowable_deviation: z.number().optional(),

  // --- version >= 2 ---
  /** Fluid zone matching tolerance, in percent */
  fluid_zone_tolerance: z.number().optional(),

  // --- version >= 7 ---
  /** Auto-update fluid zones tolerance, in percent */
  auto_update_fluid_zones_tolerance: z.number().optional(),

  // --- version >= 8 ---
  enable_temperature_propagation: z.boolean().optional(),

  // --- version >= 3 ---
  /**
   * Hidden developer/experimentation field. Still serialized in v21.0.
   * Value is typically 0. Preserved for round-trip fidelity.
   */
  experimentation_int: z.number().int().optional(),
  /**
   * Friction factor method for compressible pipes.
   * Note: incompressible_pipe_ff_method was commented out in v21.0 C++ source
   * and is NOT serialized. It does not appear in this schema.
   */
  compressible_pipe_ff_method: z.enum(['serghide', 'swamee']).optional(),

  // --- version >= 4 ---
  initial_liquid_velocity: ValueWithUnitSchema.optional(),
  initial_gas_velocity: ValueWithUnitSchema.optional(),

  // --- version >= 5 ---
  /** Energy cost rate (e.g., $/kWh) */
  energy_rate: ValueWithUnitSchema.optional(),

  // --- version >= 6 ---
  /** Cost period for energy calculations */
  cost_period: ValueWithUnitSchema.optional(),
  /** Percent of time the system operates */
  operating_time_percent: z.number().optional(),
});

export type CalculationSettings = z.infer<typeof CalculationSettingsSchema>;
